<script setup lang="ts">
  import { UTCDate } from '@date-fns/utc'
  import { format } from 'date-fns'
  import { computed } from 'vue'
  import { backgroundMap, clampTurnipPrice, typeBorders, typeColors } from '~renderer/util/island'
  import type { IslandPublicView } from '~shared/types/island'

  const props = defineProps<{
    island: IslandPublicView
    preview?: boolean
  }>()
  const rating = computed(() => props.island.averageRating ?? 0)
  const islandTime = computed(() =>
    format(new UTCDate(props.island.islandTime), 'h:mm a: EEE, MMM d, y'),
  )

  // TODO: abstract to a utility since this is used in a few spots
  const entryFeeText = computed(() => {
    if (!props.island.entryFee?.length) return

    const bellsFeeItem = props.island.entryFee.find((fee) => fee[0]?.type === 'bells')?.[0]
    const bellsFee = bellsFeeItem && 'amount' in bellsFeeItem ? bellsFeeItem.amount : 0

    const nmtFeeItem = props.island.entryFee.find((fee) => fee[0]?.type === 'nmt')?.[0]
    const nmtFee = nmtFeeItem && 'amount' in nmtFeeItem ? nmtFeeItem.amount : 0

    if (!bellsFee && !nmtFee) return

    const bellsText = `<img class="size-5" alt "Bells" src="/images/icons/bells.svg"><span class="text-tan-300"> ${bellsFee}</span>`
    const nmtText = `<img class="size-5" alt="NMT" src="/images/icons/nmt.svg"><span class="text-tan-300"> ${nmtFee}</span>`
    const orText = bellsText && nmtText ? '<span> or </span>' : ''

    return `${bellsText}${orText}${nmtText} to Enter`
  })
  const hasModifiedSwitch = computed(() => props.island.turnipPrice >= 990)
  const urlCode = computed(() =>
    props.island.customUrlTag ? `@${props.island.customUrlTag}` : props.island.turnipCode,
  )

  const backgroundClasses = computed(() => {
    const classes: string[] = []

    // type border
    classes.push(typeBorders[props.island.type])

    // text stroke
    if (props.island.customBackgroundId?.endsWith('light')) {
      classes.push('[-webkit-text-stroke:0.5px_black]')
      classes.push(
        '[&_svg_path]:stroke-black',
        '[&_svg_path]:[vector-effect:non-scaling-stroke]',
        '[&_svg_path]:[stroke-width:0.5]',
        '[&_img]:drop-shadow-[0_0_1px_black]',
      )
    }

    // background
    if (props.island.customBackgroundId) {
      const backgroundClass = backgroundMap[props.island.customBackgroundId] ?? ''
      classes.push(backgroundClass, 'bg-repeat')
    } else {
      classes.push('bg-[#554e4a]')
    }

    return classes
  })

  const getNpcImage = (npc: string) => npc.replace(/ /g, '-').replaceAll('.', '').toLowerCase()
</script>

<template>
  <a
    :href="props.preview ? '#' : `/island/${urlCode}`"
    class="group relative mt-6 flex cursor-pointer flex-col drop-shadow-xl transition-transform duration-200 hover:-translate-y-1"
    :class="props.preview ? 'pointer-events-none' : ''"
    data-prefetch-static-assets="false"
  >
    <!-- Tab / Type Badge -->
    <div
      class="absolute -top-4 -left-2 z-10 rounded-xl p-[.35rem] px-6 text-xs font-black tracking-widest uppercase shadow-sm"
      :class="typeColors[island.type] || 'bg-gray-400 text-black'"
    >
      {{ island.type }}
    </div>
    <div
      v-if="hasModifiedSwitch"
      class="absolute -top-4 -right-2 z-10 rounded-xl bg-orange-300 p-[.35rem] px-6 text-xs font-black tracking-widest uppercase shadow-sm"
    >
      Modified Switch
    </div>

    <!-- Main Card Body -->
    <div
      class="relative flex h-72 flex-col gap-4 rounded-xl border-2 p-6 text-base text-white"
      :class="backgroundClasses"
    >
      <!-- Header Section -->
      <div class="flex items-center gap-3">
        <h3 class="font-display truncate text-3xl leading-none font-bold text-white">
          {{ island.name }}
        </h3>
        <h4 class="font-display text-xl font-bold">
          By
          <a
            class="text-tan-300 hover:text-blue-400"
            target="_blank"
            :href="`/user/${island.hostId}`"
            >{{ island.hostUsername }}</a
          >
        </h4>
      </div>

      <!-- Bells & Icons Row -->
      <div class="flex items-center gap-6">
        <div class="flex items-center gap-2">
          <img src="/images/icons/turnip.svg" alt="Turnip" class="size-5" />
          <span class="text-xl font-bold">{{ clampTurnipPrice(island.turnipPrice) }} Bells</span>
        </div>

        <!-- Fruits & NPCs -->
        <div class="flex items-center gap-2">
          <img
            v-if="island.fruit"
            :src="`/images/icons/${island.fruit}.svg`"
            :title="island.fruit"
            class="size-5"
          />

          <img
            v-for="npc in island.npc"
            :src="`/images/npc/${getNpcImage(npc)}.svg`"
            :title="npc"
            class="size-5"
          />
        </div>
      </div>

      <!-- Entry Fee & Time -->
      <div class="text-base-tan flex flex-col gap-1 text-sm font-medium">
        <div
          v-if="entryFeeText"
          v-html="entryFeeText"
          class="flex flex-row items-center gap-1 align-middle font-medium"
        ></div>
        <div class="flex flex-row gap-1">
          <i-fa7-solid-clock class="size-5" />
          <span class="text-tan-300 truncate">{{ islandTime }}</span>
          <span class="text-base-tan">[{{ island.hemisphere == 'north' ? 'N' : 'S' }}]</span>
        </div>
      </div>

      <!-- Description -->
      <p
        class="text-tan-300 line-clamp-2 min-h-4 flex-1 text-sm leading-relaxed"
        :class="entryFeeText ? 'line-clamp-3' : 'line-clamp-2'"
      >
        {{ island.description }}
      </p>

      <!-- Footer Stats -->
      <div class="mt-2 flex items-center justify-between">
        <div class="text-base-tan flex items-center gap-6 text-2xl font-bold">
          <!-- Visitors -->
          <div class="flex items-center gap-2">
            <i-te-crowd class="size-6" />
            <span class="text-trim-end text-lg">{{ island.currentVisitorCount }}</span>
          </div>

          <!-- Queue -->
          <div class="flex items-center gap-2">
            <i-fa7-solid-hourglass-start />
            <span class="text-lg"
              ><span class="text-white">{{ island.currentWaitingCount }}</span
              >/{{ island.queueLimit }}</span
            >
          </div>
        </div>

        <!-- Ratings -->
        <ul class="flex">
          <li
            v-for="i in 5"
            :key="i"
            class="size-5"
            :class="i <= rating ? 'text-yellow-400' : 'text-[#000]/40'"
          >
            <i-fa7-solid-star />
          </li>
        </ul>
      </div>
    </div>
  </a>
</template>
